import React, { useState, FormEvent, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import logo from './REDISOLVE LOGO_1.png';
import backgroundVideo from './0_Abstract_Background_3840x2160.mp4';


interface FormData {
  fullName: string;
  email: string;
  mobile: string;
  scheduledDate: string;
  scheduledTime: string;
  resume: File | null;
}



interface TimeSlot {
  value: string;
  label: string;
  isAvailable: boolean;
}

// CAPTCHA Component
const Captcha: React.FC<{ onVerify: (status: boolean) => void }> = ({ onVerify }) => {
  const [captchaText, setCaptchaText] = useState<string>('');
  const [userInput, setUserInput] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);

  const generateCaptchaText = () => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };

  const refreshCaptcha = () => {
    setCaptchaText(generateCaptchaText());
    setUserInput('');
    setIsValid(false);
    onVerify(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setUserInput(input);
    const valid = input === captchaText;
    setIsValid(valid);
    onVerify(valid);
  };

  useEffect(() => {
    refreshCaptcha();
  }, []);

  return (
    <div className="captcha-container">
      <div className="d-flex align-items-center justify-content-center mb-2">
        <div 
          className="captcha-text p-3 me-2" 
          style={{
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '24px',
            letterSpacing: '4px',
            textDecoration: 'line-through',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Add random lines for additional complexity */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            borderTop: '1px solid #666',
            transform: 'rotate(-5deg)'
          }}></div>
          <div style={{
            position: 'absolute',
            top: '30%',
            left: 0,
            right: 0,
            borderTop: '1px solid #666',
            transform: 'rotate(5deg)'
          }}></div>
          {captchaText}
        </div>
        <button 
          type="button" 
          className="btn btn-outline-secondary"
          onClick={refreshCaptcha}
        >
          ↻
        </button>
      </div>
      <div className="input-group">
        <input
          type="text"
          className={`form-control ${userInput && (isValid ? 'is-valid' : 'is-invalid')}`}
          placeholder="Enter the code above"
          value={userInput}
          onChange={handleInputChange}
        />
        <div className="valid-feedback">Correct!</div>
        <div className="invalid-feedback">Incorrect code, please try again</div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    mobile: '',
    scheduledDate: '',
    scheduledTime: '10:00',
    resume: null
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showSundayModal, setShowSundayModal] = useState<boolean>(false);
  const [showDateRangeModal, setShowDateRangeModal] = useState<boolean>(false);
  const [dateRangeMessage, setDateRangeMessage] = useState<string>('');
  const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlot[]>([]);
  const [fileError, setFileError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>('');
  const [mobileError, setMobileError] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');

  const validateEmail = (email: string) => {
    // Standard email regex pattern
    const isValid = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
    setEmailError(isValid ? '' : 'Please enter a valid email address.');
    return isValid;
  };

const validateMobile = (mobile: string) => {
  const isValid = /^[6-9]\d{9}$/.test(mobile);
  setMobileError(isValid ? '' : 'Please enter a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9.');
  return isValid;
};



  const handleCaptchaChange = (token: string | null) => {
    setIsCaptchaVerified(!!token);
  };


  // Initialize time slots
  const initializeTimeSlots = (): TimeSlot[] => {
    return Array.from({ length: 8 }, (_, i) => {
      const hour = i + 10;
      return {
        value: `${hour}:00`,
        label: `${hour <= 12 ? hour : hour - 12}:00 ${hour < 12 ? 'AM' : 'PM'}`,
        isAvailable: true
      };
    });
  };

  // Fetch available time slots when date changes
  useEffect(() => {
    const fetchAvailableSlots = async () => {
      if (!formData.scheduledDate) return;

      try {
        const response = await fetch(
          `https://careers.redisolve.com:7531/api/Interview/available-slots?date=${formData.scheduledDate}`
        );
        
        if (response.ok) {
          const data = await response.json();
          const unavailableHours = new Set(data.unavailableHours);
          
          setAvailableTimeSlots(initializeTimeSlots().map(slot => ({
            ...slot,
            isAvailable: !unavailableHours.has(parseInt(slot.value.split(':')[0]))
          })));
        }
      } catch (error) {
        console.error('Error fetching available slots:', error);
      }
    };

    fetchAvailableSlots();
  }, [formData.scheduledDate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError('');
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const fileType = file.type;
      
      if (fileType !== 'application/pdf') {
        setFileError('Please upload only PDF files');
        e.target.value = '';
        setFormData(prev => ({
          ...prev,
          resume: null
        }));
        return;
      }

      setFormData(prev => ({
        ...prev,
        resume: file
      }));
    }
  };


  const resetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      mobile: '',
      scheduledDate: '',
      scheduledTime: '10:00',
      resume: null

    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Validate all fields before submission
  
  const isEmailValid = validateEmail(formData.email);
  const isMobileValid = validateMobile(formData.mobile);

  if ( !isEmailValid || !isMobileValid) {
    setLoading(false);
    return;
  }
    setLoading(true);
    setMessage('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('mobile', formData.mobile);
      const combinedDateTime = `${formData.scheduledDate} ${formData.scheduledTime}`;
      formDataToSend.append('scheduledDate', combinedDateTime);
      
      if (formData.resume) {
        formDataToSend.append('resume', formData.resume);
      }

      const response = await fetch('https://careers.redisolve.com:7531/api/Interview/schedule', {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (response.ok) {
        setMessage('Interview scheduled successfully!');
        setShowModal(true);
        resetForm();
      } else {
        setMessage(result.message || 'Failed to schedule interview');
      }
    } catch (error) {
      setMessage('An error occurred while scheduling the interview');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Date-related functions
  const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const get15DaysFromTomorrow = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 15);
    return maxDate.toISOString().split('T')[0];
  };

  const isSunday = (dateString: string) => {
    const date = new Date(dateString);
    return date.getDay() === 0;
  };

  const isDateInRange = (dateString: string) => {
    const selectedDate = new Date(dateString);
    const tomorrow = new Date(getTomorrow());
    const maxDate = new Date(get15DaysFromTomorrow());
    return selectedDate >= tomorrow && selectedDate <= maxDate;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    
    if (isSunday(value)) {
      setShowSundayModal(true);
      e.target.value = formData.scheduledDate;
      return;
    }

    if (!isDateInRange(value)) {
      const tomorrow = new Date(getTomorrow());
      const maxDate = new Date(get15DaysFromTomorrow());
      const selectedDate = new Date(value);
      
      if (selectedDate < tomorrow) {
        setDateRangeMessage("Please select a future date starting from tomorrow.");
      } else if (selectedDate > maxDate) {
        setDateRangeMessage("Interviews can only be scheduled within the next 15 days.");
      }
      
      setShowDateRangeModal(true);
      e.target.value = formData.scheduledDate;
      return;
    }

    setFormData(prev => ({
      ...prev,
      scheduledDate: value
    }));
  };

  const displayedTimeSlots = availableTimeSlots.filter(slot => slot.isAvailable);
  
  // Add this to your component to set custom validation
  useEffect(() => {
    const dateInput = document.getElementById('scheduledDate') as HTMLInputElement;
    if (dateInput) {
      // Add custom validation to prevent Sunday selection
      dateInput.addEventListener('input', function() {
        if (isSunday(this.value)) {
          this.setCustomValidity("Sunday appointments are not available");
        } else {
          this.setCustomValidity("");
        }
      });
    }
    
    return () => {
      // Cleanup listener on component unmount
      const dateInput = document.getElementById('scheduledDate') as HTMLInputElement;
      if (dateInput) {
        dateInput.removeEventListener('input', () => {});
      }
    };
  }, []);

  return (
    // <div className="container-fluid" 
    // style={{
    //   backgroundImage: `url(${backgroundImage})`, 
    //   backgroundSize: 'cover',
    //   backgroundPosition: 'center',
    //   backgroundAttachment: 'fixed',
    //   minHeight: '100vh',
    //   padding: '20px'
    // }}>
    <div className="container-fluid position-relative" style={{ minHeight: '100vh', padding: '20px' }}>
    <video 
      autoPlay 
      muted 
      loop 
      playsInline 
      style={{ 
        position: 'fixed',
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        objectFit: 'cover',
        margin: 'auto',
        left: '50%',
        transform: 'translateX(-50%)',
      }}
    >
      <source src={backgroundVideo} type="video/mp4" />
      Your browser does not support the video tag.
    </video>

    {/* Success Modal */}
    <div className={`modal fade ${showModal ? 'show' : ''}`} 
         style={{ display: showModal ? 'block' : 'none' }}
         tabIndex={-1}
         role="dialog">
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '300px' }}>
        <div className="modal-content">
          <div className="modal-body text-center py-4">
            <p className="mb-0">{message}</p>
          </div>
          <div className="modal-footer justify-content-center border-0 pb-4">
            <button type="button" 
              className="btn btn-primary px-4"
              onClick={() => {
                setShowModal(false);
                window.location.reload();
              }}>
              OK
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Modal backdrop */}
    {showModal && <div className="modal-backdrop fade show"></div>}

    {/* Sunday Modal */}
    <div className={`modal fade ${showSundayModal ? 'show' : ''}`} 
         style={{ display: showSundayModal ? 'block' : 'none' }}
         tabIndex={-1}
         role="dialog">
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Unavailable Date</h5>
            <button type="button" className="btn-close" onClick={() => setShowSundayModal(false)}></button>
          </div>
          <div className="modal-body text-center py-4">
            <i className="bi bi-calendar-x text-danger" style={{ fontSize: '2rem' }}></i>
            <p className="mt-3 mb-0">Sunday appointments are not available. Please select another day.</p>
          </div>
          <div className="modal-footer justify-content-center border-0 pb-4">
            <button type="button" 
                    className="btn btn-primary px-4"
                    onClick={() => setShowSundayModal(false)}>
              OK
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Date Range Modal */}
    <div className={`modal fade ${showDateRangeModal ? 'show' : ''}`} 
         style={{ display: showDateRangeModal ? 'block' : 'none' }}
         tabIndex={-1}
         role="dialog">
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Invalid Date Selection</h5>
            <button type="button" className="btn-close" onClick={() => setShowDateRangeModal(false)}></button>
          </div>
          <div className="modal-body text-center py-4">
            <i className="bi bi-calendar-x text-danger" style={{ fontSize: '2rem' }}></i>
            <p className="mt-3 mb-0">{dateRangeMessage}</p>
          </div>
          <div className="modal-footer justify-content-center border-0 pb-4">
            <button type="button" 
                    className="btn btn-primary px-4"
                    onClick={() => setShowDateRangeModal(false)}>
              OK
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Modal backdrops */}
    {showSundayModal && <div className="modal-backdrop fade show"></div>}
    {showDateRangeModal && <div className="modal-backdrop fade show"></div>}
      
      
      {showModal && (
        <div className="modal-backdrop fade show"></div>
      )}
{/* Sunday Unavailable Modal */}
<div className={`modal fade ${showSundayModal ? 'show' : ''}`} 
     style={{ display: showSundayModal ? 'block' : 'none' }}
     tabIndex={-1}
     role="dialog">
  <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
    <div className="modal-content">
      <div className="modal-header">
        <h5 className="modal-title">Unavailable Date</h5>
        <button type="button" className="btn-close" onClick={() => setShowSundayModal(false)}></button>
      </div>
      <div className="modal-body text-center py-4">
        <i className="bi bi-calendar-x text-danger" style={{ fontSize: '2rem' }}></i>
        <p className="mt-3 mb-0">Sunday appointments are not available. Please select another day.</p>
      </div>
      <div className="modal-footer justify-content-center border-0 pb-4">
        <button type="button" 
                className="btn btn-primary px-4"
                onClick={() => setShowSundayModal(false)}>
          OK
        </button>
      </div>
    </div>
  </div>
</div>

{/* Modal backdrop for Sunday modal */}
{showSundayModal && (
  <div className="modal-backdrop fade show"></div>
)}
      {/* First Row */}
      
       {/* Updated First Row */}
       <div className="row mb-4">
        <div className="col-12">
          {/* Desktop View */}
          <div className="d-none d-md-flex align-items-center justify-content-center position-relative py-3">
            <div style={{ position: 'absolute', left: '0' }}>
              <img src={logo} alt="Company Logo" style={{ height: '50px', maxWidth: '100%' }} />
            </div>
            <h1
              className="m-0 text-center flex-grow-1"
              style={{
                fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
                fontWeight: 'bold'
              }}
            >
              <span style={{ color: '#193C69' }}> Careers</span>
            </h1>
          </div>

          {/* Mobile View */}
          <div className="d-md-none">
            <div className="text-center mb-3">
              <img 
                src={logo} 
                alt="Company Logo" 
                style={{ 
                  height: '60px', 
                  maxWidth: '100%', 
                  borderRadius: '20px'
                }} 
              />
            </div>
            <h1
              className="text-center"
              style={{
                fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
                fontWeight: 'bold'
              }}
            >
              <span style={{ color: '#193C69' }}> Careers</span>
            </h1>
          </div>
        </div>
      </div>

      {/* Second Row with Form */}
      <div className="row d-flex">
      <div className="col-md-6 mb-4 mb-md-0 pe-md-4 d-flex">
          <div className="card w-100">
            <div className="card-header">
              <h3 className="text-center">Job Description: E-Relationship Associate</h3>
            </div>
            <div className="card-body d-flex flex-column">
                <h6 className="text-primary">Roles and Responsibilities:</h6>
                
                <ul>
                  <li>Promptly and professionally respond to customer inquiries via chat and email.</li>
                  <li>Efficiently resolve customer concerns and ensure a seamless service experience.</li>
                  <li>Diagnose and troubleshoot technical issues, providing effective solutions.</li>
                  <li>Exhibit strong customer-handling skills with a focus on empathy and problem resolution.</li>
                  <li>Escalate unresolved concerns to the appropriate internal teams for further support.</li>
                  <li>Deliver accurate and up-to-date information on products and services.</li>
                  <li>Maintain exceptional service quality by adhering to organizational standards and best practices.</li>
                  
                </ul>

                <h6 className="text-primary">Work Location:</h6>
                <p>Chennai, Salem – Work from Office</p>

                <h6 className="text-primary">Working Hours:</h6>
                <p>Rotational shifts, including night shifts.</p>

                <h6 className="text-primary">Important Notes:</h6>
                <ul>
                  <li>Phase 1: Written test</li>
                  <li>Phase 2: Interview</li>                  
                </ul>
                <p>Candidates are advised to arrive at least 10 minutes prior to the scheduled time to ensure a smooth selection process.</p>
                <p style={{ color: 'red', fontWeight: 'bold' }}>Disclaimer: Redisolve Software Pvt. Ltd. does not charge any fees or accept money in exchange for job placements or employment opportunities. Any individual or entity requesting payment for job placement services does not represent Redisolve and is not authorized to act on our behalf. We are not liable for any loss, fraud, or other issues resulting from interactions with unauthorized parties. Please contact us directly through our official channels for any inquiries related to job opportunities.</p>
            </div>
          </div>
        </div>
        
        <div className="col-md-6 ps-md-4 d-flex">
        <div className="card w-100">
            <div className="card-header">
              <h3 className="text-center">Schedule Phase 1 </h3>
            </div>
            <div className="card-body">             
              
              <form onSubmit={handleSubmit}>

              <div className="row mb-3">
  <div className="col-md-4">
    <label htmlFor="fullName" className="form-label pt-2">Full Name</label>
  </div>
  <div className="col-md-8">
    <input
      type="text"
      className={`form-control ${nameError && 'is-invalid'}`}
      id="fullName"
      name="fullName"
      value={formData.fullName}
      onChange={handleInputChange}      
      required
    />
    {nameError && <div className="invalid-feedback">{nameError}</div>}
  </div>
</div>

                <div className="row mb-3">
  <div className="col-md-4">
    <label htmlFor="email" className="form-label pt-2">Email</label>
  </div>
  <div className="col-md-8">
    <input
      type="email"
      className={`form-control ${emailError && 'is-invalid'}`}
      id="email"
      name="email"
      value={formData.email}
      onChange={handleInputChange}
      onBlur={(e) => validateEmail(e.target.value)}
      required
    />
    {emailError && <div className="invalid-feedback">{emailError}</div>}
  </div>
</div>

<div className="row mb-3">
  <div className="col-md-4">
    <label htmlFor="mobile" className="form-label pt-2">Mobile</label>
  </div>
  <div className="col-md-8">
    <input
      type="tel"
      className={`form-control ${mobileError && 'is-invalid'}`}
      id="mobile"
      name="mobile"
      value={formData.mobile}
      onChange={handleInputChange}
      onBlur={(e) => validateMobile(e.target.value)}
      required
      maxLength={10}
    />
    {mobileError && <div className="invalid-feedback">{mobileError}</div>}
  </div>
</div>
{/* ssssssssssssssssssssssssssss */}
<div className="row mb-3">
                  <div className="col-md-4">
                    <label htmlFor="scheduledDate" className="form-label pt-2">Schedule Slot </label>
                  </div>
                  <div className="col-12 col-md-8">
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3 mb-md-0">
                        <input
                          type="date"
                          className="form-control"
                          id="scheduledDate"
                          name="scheduledDate"
                          value={formData.scheduledDate}
                          onChange={handleDateChange}
                          min={getTomorrow()}
                          max={get15DaysFromTomorrow()}
                          required
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <select
                          className="form-control"
                          id="scheduledTime"
                          name="scheduledTime"
                          value={formData.scheduledTime}
                          onChange={handleInputChange}
                          required
                        >
                          {displayedTimeSlots.map(slot => (
                            <option key={slot.value} value={slot.value}>
                              {slot.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <label htmlFor="resume" className="form-label pt-2">Resume</label>
                  </div>
                  <div className="col-md-8">
                  <input
                      type="file"
                      className="form-control"
                      id="resume"
                      name="resume"
                      onChange={handleFileChange}
                      accept=".pdf"
                      required
                      ref={fileInputRef}
                    />
                    {fileError && (
                      <div className="text-danger mt-1 small">
                        {fileError}
                      </div>
                    )}
                  </div>
                </div>

                {/* Add reCAPTCHA */}
                <div className="row mb-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <label className="form-label mb-3">Please verify you are human</label>
                <Captcha onVerify={setIsCaptchaVerified} />
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading || !isCaptchaVerified}
          >
            {loading ? 'Scheduling...' : 'Schedule '}
          </button>
          
        </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;